import React, {FC, Fragment, useEffect, useState} from 'react'
import {Button, Form, Spinner} from 'react-bootstrap'
import CopyToClipboard from 'react-copy-to-clipboard'
import {EmailTemplateShortcode} from 'src/interfaces/EmailTemplate.interface'
import {getEmailTemplateShortcodesAsync} from 'src/redux/actions/emailTemplateActions'
import {useAppDispatch} from 'src/redux/storeHooks'
import AppCustomEditor from 'src/utils/CustomEditor'

export const EMAIL_TEMPLATE_TYPES = [
  {value: 'DOWNTIME', label: 'Downtime / Maintenance'},
  {value: 'ANNOUNCEMENT', label: 'Feature Announcement'},
  {value: 'FEATURE', label: 'Product Feature'},
  {value: 'REPORT', label: 'Performance Report'},
  {value: 'MAINTENANCE', label: 'Emergency Maintenance'},
  {value: 'CUSTOM', label: 'Custom'},
] as const

export type EmailTemplateFormValues = {
  type: string
  name: string
  template_data: string
  is_active: boolean
}

const parseEmailTemplateShortcodes = (payload: unknown): EmailTemplateShortcode[] => {
  const shortcodes = new Map<string, EmailTemplateShortcode>()

  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit)
      return
    }

    if (typeof value === 'string') {
      if (/^\[[A-Z0-9_]+\]$/.test(value)) {
        shortcodes.set(value, {shortcode: value})
      }
      return
    }

    if (!value || typeof value !== 'object') return

    const item = value as Record<string, unknown>
    if (typeof item.shortcode === 'string') {
      const shortcode = item.shortcode
      shortcodes.set(shortcode, {
        key: typeof item.key === 'string' ? item.key : undefined,
        label: typeof item.label === 'string' ? item.label : undefined,
        category: typeof item.category === 'string' ? item.category : undefined,
        shortcode,
        description: typeof item.description === 'string' ? item.description : undefined,
      })
      return
    }

    Object.values(item).forEach(visit)
  }

  visit(payload)
  return Array.from(shortcodes.values())
}

export const emptyEmailTemplateForm = (): EmailTemplateFormValues => ({
  type: '',
  name: '',
  template_data: '',
  is_active: true,
})

type Props = {
  values: EmailTemplateFormValues
  onChange: (field: keyof EmailTemplateFormValues, value: string | boolean) => void
  onSubmit: (event: React.FormEvent) => void
  submitting?: boolean
  formId?: string
  isEdit?: boolean
}

const EmailTemplateForm: FC<Props> = ({
  values,
  onChange,
  onSubmit,
  submitting = false,
  formId = 'email-template-form',
  isEdit = false,
}) => {
  const dispatch = useAppDispatch()
  const [copiedPlaceholder, setCopiedPlaceholder] = useState('')
  const [shortcodes, setShortcodes] = useState<EmailTemplateShortcode[]>([])
  const [loadingShortcodes, setLoadingShortcodes] = useState(true)
  const [shortcodeLoadFailed, setShortcodeLoadFailed] = useState(false)

  useEffect(() => {
    let isMounted = true

    dispatch(getEmailTemplateShortcodesAsync())
      .then((response) => {
        if (!isMounted) return

        if (getEmailTemplateShortcodesAsync.rejected.match(response)) {
          setShortcodeLoadFailed(true)
          return
        }

        const parsedShortcodes = parseEmailTemplateShortcodes(response.payload)
        setShortcodes(parsedShortcodes)
        setShortcodeLoadFailed(parsedShortcodes.length === 0)
      })
      .catch(() => {
        if (isMounted) setShortcodeLoadFailed(true)
      })
      .finally(() => {
        if (isMounted) setLoadingShortcodes(false)
      })

    return () => {
      isMounted = false
    }
  }, [dispatch])

  const handleCopyPlaceholder = (placeholder: string) => {
    setCopiedPlaceholder(placeholder)
    window.setTimeout(() => setCopiedPlaceholder(''), 1000)
  }

  return (
    <Form id={formId} className='superadmin-email-template-form' onSubmit={onSubmit}>
      <div className='email-template-variables-hint mb-6'>
        <strong>Available dynamic placeholders:</strong>{' '}
        {loadingShortcodes ? (
          <span>
            <Spinner animation='border' size='sm' className='me-2' />
            Loading placeholders...
          </span>
        ) : shortcodeLoadFailed ? (
          <span>Unable to load dynamic placeholders.</span>
        ) : (
          <>
            Use tags like{' '}
            {shortcodes.map((item, index) => (
              <Fragment key={item.shortcode}>
                {index > 0 ? ', ' : null}
                <CopyToClipboard text={item.shortcode}>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline-secondary'
                    title={item.description || item.label}
                    className={`email-template-shortcode-btn px-2 py-1 m-0 ${
                      copiedPlaceholder === item.shortcode ? 'is-copied' : ''
                    }`}
                    onClick={() => handleCopyPlaceholder(item.shortcode)}
                  >
                    {copiedPlaceholder === item.shortcode ? 'Copied!' : item.shortcode}
                  </Button>
                </CopyToClipboard>
              </Fragment>
            ))}{' '}
            to auto-populate data during bulk email sends.
          </>
        )}
      </div>

      <div className='fv-row mb-7'>
        <Form.Label htmlFor={`${formId}-type`} className='required fs-6 fw-semibold mb-2'>
          Template Name (Internal Reference)
        </Form.Label>
        <Form.Control
          id={`${formId}-type`}
          type='text'
          placeholder='e.g., Downtime / Maintenance'
          value={values.type}
          onChange={(e) => onChange('type', e.target.value)}
          required
        />
      </div>

      <div className='fv-row mb-7'>
        <Form.Label htmlFor={`${formId}-name`} className='required fs-6 fw-semibold mb-2'>
          Email Subject Line
        </Form.Label>
        <Form.Control
          id={`${formId}-name`}
          type='text'
          placeholder='e.g., Important updates regarding your Commercium sync configurations'
          value={values.name}
          onChange={(e) => onChange('name', e.target.value)}
          required
        />
      </div>

      <div className='fv-row mb-7'>
        <Form.Label className='required fs-6 fw-semibold mb-2'>
          Email Body Content (Support HTML/Plain Text)
        </Form.Label>
        <div className='email-template-editor'>
          <AppCustomEditor
            value={values.template_data}
            onChangeData={(html) => onChange('template_data', html)}
            placeholder='Hi [USERNAME], we wanted to let you know that...'
            handleHtmlPaste
          />
        </div>
      </div>

      {isEdit ? (
        <div className='fv-row mb-0'>
          <Form.Check
            type='switch'
            id={`${formId}-active`}
            label='Active'
            checked={values.is_active}
            onChange={(e) => onChange('is_active', e.target.checked)}
          />
        </div>
      ) : null}

      {submitting ? (
        <span className='visually-hidden' aria-live='polite'>
          Saving...
        </span>
      ) : null}
    </Form>
  )
}

export default EmailTemplateForm
